import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { passwordResetAPI } from '../utils/api';

const LogoIc = () => (
  <svg width="48" height="48" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Q-Lana TMS">
    <rect width="40" height="40" rx="9" fill="#1A2F5E"/>
    <line x1="9" y1="13" x2="27" y2="13" stroke="white" strokeWidth="3" strokeLinecap="round"/>
    <line x1="9" y1="20" x2="27" y2="20" stroke="white" strokeWidth="3" strokeLinecap="round"/>
    <line x1="9" y1="27" x2="20" y2="27" stroke="white" strokeWidth="3" strokeLinecap="round"/>
    <polyline points="21,24 25,28 33,18" stroke="#5CC3BC" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export default function ResetPasswordPage() {
  const [params]  = useSearchParams();
  const token     = params.get('token') || '';
  // Cosmetic only: wording differs, the server validates the token the same way.
  const invited   = params.get('invite') === '1';
  const [newPw,   setNewPw]   = useState('');
  const [confirm, setConfirm] = useState('');
  const [done,    setDone]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return setError('Password must be at least 8 characters');
    if (newPw !== confirm) return setError('Passwords do not match');
    setLoading(true); setError('');
    try {
      await passwordResetAPI.reset(token, newPw);
      setDone(true);
    } catch(e) {
      setError(e.response?.data?.message || 'Invalid or expired reset link.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background:'var(--bg)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <LogoIc />
            <h1 className="text-2xl font-bold" style={{ color:'var(--text)' }}>Q-Lana TMS</h1>
          </div>
          <p className="text-sm" style={{ color:'var(--text-secondary)' }}>
            {done ? (invited ? 'Account ready' : 'Password reset complete')
              : invited ? 'Welcome! Set your password to get started' : 'Set a new password'}
          </p>
        </div>

        <div className="card p-8">
          {done ? (
            <div className="text-center space-y-4 py-4">
              <div className="text-5xl">✅</div>
              <h2 className="text-lg font-semibold" style={{ color:'var(--text)' }}>{invited ? 'Your password is set' : 'Password reset successfully'}</h2>
              <p className="text-sm" style={{ color:'var(--text-secondary)' }}>{invited ? 'Welcome aboard. You can now sign in.' : 'You can now sign in with your new password.'}</p>
              <Link to="/login" className="btn-primary inline-block px-8">Sign in now</Link>
            </div>
          ) : !token ? (
            <div className="text-center space-y-4 py-4">
              <h2 className="text-lg font-semibold" style={{ color:'var(--text)' }}>Reset link is incomplete</h2>
              <p className="text-sm" style={{ color:'var(--text-secondary)' }}>Request a new link and open it from your email.</p>
              <Link to="/forgot-password" className="btn-primary inline-block px-8">Request a new link</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {invited && (
                <div>
                  <h2 className="text-lg font-semibold mb-1" style={{ color:'var(--text)' }}>Welcome to Q-Lana TMS</h2>
                  <p className="text-sm" style={{ color:'var(--text-secondary)' }}>
                    Your account is ready. Choose a password to finish setting it up.
                  </p>
                </div>
              )}
              <div>
                <label className="label">New Password <span className="text-red-500">*</span></label>
                <input type="password" className="input" value={newPw} autoFocus
                  onChange={e => setNewPw(e.target.value)} placeholder="Minimum 8 characters"/>
              </div>
              <div>
                <label className="label">Confirm Password <span className="text-red-500">*</span></label>
                <input type="password" className="input" value={confirm}
                  onChange={e => setConfirm(e.target.value)} placeholder="Repeat your new password"/>
              </div>
              {error && (
                <p className="text-sm text-red-500">
                  {error} <Link to="/forgot-password" className="underline">Request a new link</Link>
                </p>
              )}
              <button type="submit" disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2">
                {loading && <span className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin"/>}
                {invited ? 'Set Password' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
